import { useEffect, useRef } from 'react';

/**
 * DiamondSwab — faceted brilliant-cut diamond with per-channel chromatic
 * dispersion. Inspired by Maxime Heckel's refraction/dispersion breakdown and
 * the "Cheap Diamond Rendering" technique (Viktor Zatorskyi).
 *
 * The trick that sells a diamond is *per-RGB-channel refraction*: each colour
 * channel uses a slightly different IOR, so when we sample a bright background
 * (here a procedural light field) through the diamond's normal, the channels
 * pick up colour from different directions — that's the rainbow "fire".
 *
 * Pipeline (all in 2D, no raytracing):
 *   1. Disk mask defines the stone outline; outside is the swab background.
 *   2. Each pixel inside the disk gets a fake spherical normal (bulging
 *      toward the viewer) perturbed by Voronoi facet jitter.
 *   3. refract() is called three times with different IORs → R/G/B sampling
 *      directions, then a procedural bright field is sampled with each.
 *   4. Strong Fresnel at the disk's silhouette produces the diamond's bright
 *      rim glow.
 *   5. Sharp specular pow() peaks at facet centres give brilliance.
 *   6. Cursor steers the light source — hover to make the stone catch light.
 */

const VERT = `
attribute vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform float u_time;
uniform vec2 u_resolution;
uniform vec2 u_mouse;
uniform float u_mouseActive;

vec3 hash3(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xxy + p3.yzz) * p3.zyx);
}

// Voronoi: returns owning-cell id (xy), distance to cell centre (z),
// approximate distance to nearest cell boundary (w).
vec4 voronoi(vec2 p, float t) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 mPos = vec2(0.0);
  vec2 mOff = vec2(0.0);
  float md = 8.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 g = vec2(float(x), float(y));
      vec2 o = hash3(i + g).xy;
      o = 0.5 + 0.4 * sin(t * 0.3 + 6.28318 * o);
      vec2 r = g + o - f;
      float d = dot(r, r);
      if (d < md) { md = d; mPos = i + g; mOff = g + o; }
    }
  }
  float mEdge = 8.0;
  for (int y = -2; y <= 2; y++) {
    for (int x = -2; x <= 2; x++) {
      vec2 g = vec2(float(x), float(y));
      vec2 o = hash3(i + g).xy;
      o = 0.5 + 0.4 * sin(t * 0.3 + 6.28318 * o);
      vec2 r = g + o - f;
      vec2 diff = r - mOff + f;
      if (dot(diff, diff) > 0.00001) {
        float d = dot(0.5 * (mOff + r - f + f), normalize(r - (mOff - f)));
        mEdge = min(mEdge, d);
      }
    }
  }
  return vec4(mPos, sqrt(md), mEdge);
}

// Procedural "light field" the diamond refracts. A handful of bright coloured
// hotspots — drifting slowly — that produce the rainbow flashes as the per-
// channel refraction picks up different bright sources.
vec3 lightField(vec2 uv, float t) {
  vec3 col = vec3(0.02);
  // Big warm key light
  vec2 k = vec2(0.5 + 0.3 * cos(t * 0.2), 0.7 + 0.1 * sin(t * 0.15));
  col += vec3(1.4, 1.2, 0.9) * exp(-12.0 * dot(uv - k, uv - k));
  // Cool fill from the left
  vec2 f1 = vec2(-0.25 + 0.15 * sin(t * 0.18), 0.0);
  col += vec3(0.5, 0.8, 1.4) * exp(-15.0 * dot(uv - f1, uv - f1));
  // Magenta accent bottom-right
  vec2 f2 = vec2(0.35 + 0.1 * cos(t * 0.25), -0.3);
  col += vec3(1.3, 0.5, 0.9) * exp(-18.0 * dot(uv - f2, uv - f2));
  // Subtle cyan rim
  vec2 f3 = vec2(-0.3 + 0.12 * cos(t * 0.3), -0.4);
  col += vec3(0.4, 1.2, 1.0) * exp(-20.0 * dot(uv - f3, uv - f3));
  return col;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 p = (uv - 0.5);
  float t = u_time;

  // -- Diamond shape: a disk filling most of the canvas --
  float R = 0.46;
  float r = length(p);
  float disk = smoothstep(R + 0.004, R - 0.004, r);

  // -- Fake spherical normal: bulges toward camera at the centre --
  float zCenter = sqrt(max(0.0, R * R - r * r)) / R;       // 0 at edge, 1 at centre
  vec3 sphereN = normalize(vec3(p / R, zCenter + 0.0001));

  // -- Voronoi facet perturbation: rotates slowly so the stone feels alive --
  float ang = t * 0.08;
  mat2 rot = mat2(cos(ang), -sin(ang), sin(ang), cos(ang));
  vec4 vor = voronoi(rot * p * 9.0, t);
  vec2 cellId = vor.xy;
  vec3 facetJitter = hash3(cellId * 1.7) * 2.0 - 1.0;
  facetJitter.z *= 0.5;
  // Mix the smooth sphere normal with the per-facet jitter. The mix weight
  // is smaller near the edge so the silhouette stays smooth and Fresnel reads.
  float facetWeight = 0.55 * smoothstep(0.05, 0.6, zCenter);
  vec3 N = normalize(sphereN + facetJitter * facetWeight);

  // -- Eye vector --
  vec3 V = vec3(0.0, 0.0, 1.0);

  // -- Per-channel refraction: real diamond IOR ≈ 2.42 → ratio ≈ 0.41.
  // Splitting the ratio slightly per channel creates the "fire" rainbow. --
  float etaR = 0.43;
  float etaG = 0.41;
  float etaB = 0.39;
  vec3 refR = refract(V, N, etaR);
  vec3 refG = refract(V, N, etaG);
  vec3 refB = refract(V, N, etaB);

  // Sample the light field through the refracted direction. Two bounces
  // (sample at + offset, then + a second offset) cheaply fake internal
  // ricochets.
  float refractStrength = 0.45;
  vec2 baseUV = p * 1.3;
  vec3 sampleR = lightField(baseUV + refR.xy * refractStrength, t)
               + 0.45 * lightField(baseUV + refR.xy * refractStrength * 1.7 + vec2(0.1, -0.05), t);
  vec3 sampleG = lightField(baseUV + refG.xy * refractStrength, t)
               + 0.45 * lightField(baseUV + refG.xy * refractStrength * 1.7 + vec2(0.1, -0.05), t);
  vec3 sampleB = lightField(baseUV + refB.xy * refractStrength, t)
               + 0.45 * lightField(baseUV + refB.xy * refractStrength * 1.7 + vec2(0.1, -0.05), t);

  vec3 dispersed = vec3(sampleR.r, sampleG.g, sampleB.b);

  // -- Specular highlights driven by a key light (cursor steers it) --
  vec3 sunIdle = normalize(vec3(0.35 * cos(t * 0.25), 0.35 * sin(t * 0.3), 0.85));
  vec3 sunMouse = normalize(vec3((u_mouse - 0.5) * 1.3, 0.7));
  vec3 L = normalize(mix(sunIdle, sunMouse, u_mouseActive));
  float NdotL = max(0.0, dot(N, L));
  float spec = pow(NdotL, 32.0);

  // -- Pinpoint sparkle at facet centres on the brightest facets --
  float center = 1.0 - smoothstep(0.0, 0.07, vor.z);
  float sparkle = center * pow(NdotL, 6.0) * 1.6;

  // -- Thin dark fissures at cell boundaries — sells the "cut" --
  float edge = smoothstep(0.0, 0.012, vor.w);
  float fissure = mix(0.4, 1.0, edge);

  // -- Fresnel rim: bright white halo at the diamond's silhouette --
  float fres = pow(1.0 - zCenter, 3.5);

  // Compose the diamond surface colour.
  vec3 surface = dispersed * 0.95 * fissure
               + vec3(1.0) * spec * 1.4
               + vec3(1.0) * sparkle
               + vec3(0.95, 1.0, 1.0) * fres * 0.9;

  // Background outside the diamond — soft cool grey so the stone pops.
  vec3 bg = vec3(0.93, 0.95, 0.98);

  vec3 col = mix(bg, surface, disk);

  // Subtle vignette on the whole tile.
  col *= 1.0 - 0.15 * smoothstep(0.45, 0.85, length(p));

  gl_FragColor = vec4(col, 1.0);
}
`;

export default function DiamondSwab() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false });
    if (!gl) return;

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error('Diamond shader error:', gl.getShaderInfoLog(sh));
      }
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const timeLoc = gl.getUniformLocation(program, 'u_time');
    const resLoc = gl.getUniformLocation(program, 'u_resolution');
    const mouseLoc = gl.getUniformLocation(program, 'u_mouse');
    const activeLoc = gl.getUniformLocation(program, 'u_mouseActive');

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const w = Math.floor(rect.width * dpr);
      const h = Math.floor(rect.height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) / rect.width;
      mouseRef.current.y = 1 - (e.clientY - rect.top) / rect.height;
    };
    const onEnter = () => { mouseRef.current.active = 1; };
    const onLeave = () => { mouseRef.current.active = 0; };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseenter', onEnter);
    canvas.addEventListener('mouseleave', onLeave);

    const start = performance.now();
    let raf = 0;
    let activeEased = 0;
    const render = () => {
      resize();
      activeEased += (mouseRef.current.active - activeEased) * 0.12;
      gl.uniform1f(timeLoc, (performance.now() - start) / 1000);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.uniform2f(mouseLoc, mouseRef.current.x, mouseRef.current.y);
      gl.uniform1f(activeLoc, activeEased);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseenter', onEnter);
      canvas.removeEventListener('mouseleave', onLeave);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full aspect-square rounded-xl bg-slate-100" />;
}
